import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { payForSubscription, verifyTransactionPin, useWallet } from "../utils/premium";
import AnimatedBalance from "../components/AnimatedBalance";

export default function PremiumPage() {
    const [plan, setPlan] = useState("customer");
    const [billing, setBilling] = useState("monthly");
    const [showModal, setShowModal] = useState(false);
    const [userId, setUserId] = useState("");
    const [pin, setPin] = useState("");
    const [error, setError] = useState("");
    const [paymentSuccess, setPaymentSuccess] = useState(false);
    const [processing, setProcessing] = useState(false);

    const prices = {
        customer: { monthly: 4950, yearly: 53460 },
        provider: { monthly: 9900, yearly: 106920 },
    };

    const { wallet, loading } = useWallet(userId.length === 28 ? userId : "");
    const walletActive = wallet?.walletStatus === "active";

    const userWallet = {
        balance: wallet?.balance ?? 0,
        currency: wallet?.currency ?? "NGN",
    };

    const currentPrice = prices[plan][billing];
    const totalAmount = currentPrice + 100;
    const hasEnoughBalance = userWallet.balance >= totalAmount;

    const formattedBalance = new Intl.NumberFormat("en-NG", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(userWallet.balance || 0));

    const features = {
        customer: [
            "Priority Access to Top Providers",
            "Discounts on Services",
            "Priority Support",
        ],
        provider: ["Boosted Visibility", "Keep 100% Earnings", "Priority Support"],
    };

    // Handle subscription with in-UI error messages
    const handleSubscribe = async () => {
        if (!userId) {
            setError("Enter your Premium Account ID");
            return;
        }
        if (pin.length !== 4) {
            setError("Enter your 4-digit PIN");
            return;
        }
        if (!hasEnoughBalance) {
            setError("Insufficient balance");
            return;
        }

        try {
            setProcessing(true);
            await verifyTransactionPin(userId, pin);
            const res = await payForSubscription({
                userId,
                planType: plan,
                billingCycle: billing,
            });
            if (res) {
                setPaymentSuccess(true);
                setShowModal(false);
            }
        } catch (err) {
            console.error(err);
            // setError("Payment failed");
            setError(`${err}`);
        } finally {
            setProcessing(false);
        }
    };

    // Auto-close success modal
    useEffect(() => {
        if (paymentSuccess) {
            const timer = setTimeout(() => setPaymentSuccess(false), 2500);
            return () => clearTimeout(timer);
        }
    }, [paymentSuccess]);

    return (
        <div style={styles.page}>
            {/* HERO */}
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                style={styles.hero}
            >
                <h1 style={styles.title}>Upgrade to Premium</h1>
                <p style={styles.subtitle}>
                    Enjoy FixerFind Premium Offers. 🚀✅
                </p>
            </motion.div>

            {/* PLAN SWITCH */}
            <div style={styles.switch}>
                {["customer", "provider"].map((p) => (
                    <button
                        key={p}
                        style={{
                            ...styles.switchBtn,
                            background: plan === p ? "#007AFF" : "transparent",
                            color: plan === p ? "#fff" : "#d9d6d6",
                        }}
                        onClick={() => setPlan(p)}
                    >
                        {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                ))}
            </div>

            {/* USER WALLET */}
            <div style={styles.userSection}>
                <input
                    type="text"
                    placeholder="Enter your Premium Account ID"
                    value={userId}
                    onChange={(e) => {
                        setUserId(e.target.value);
                        if (error) setError("");
                    }}
                    style={styles.input}
                />

                {userId.length === 28 && (
                    <div style={styles.walletCard}>
                        {loading ? (
                            <motion.div
                                style={{
                                    height: 60,
                                    borderRadius: 12,
                                    background: "#ffffff33",
                                    overflow: "hidden",
                                    marginBottom: 10,
                                    position: "relative",
                                }}
                                animate={{ x: ["-100%", "100%"] }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            />
                        ) : (
                            <>
                                {walletActive && (<AnimatedBalance balance={userWallet.balance} />)}

                                <p style={styles.walletStatus}>
                                    {walletActive ? `Wallet: Active` : `Activate your FixerFind wallet to make payment`}
                                </p>

                                {walletActive && (<div
                                    style={{
                                        ...styles.eligibility,
                                        background: hasEnoughBalance ? "#e6f9f0" : "#ffe6e6",
                                        color: hasEnoughBalance ? "#0a7f42" : "#b00020",
                                    }}
                                >
                                    {hasEnoughBalance
                                        ? "You can proceed with payment"
                                        : "Insufficient balance"}
                                </div>)}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* PLAN CARD */}
            <motion.div
                key={plan + billing}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                style={styles.card}
            >
                <h2 style={styles.planTitle}>
                    {plan === "customer" ? "Customer Premium" : "Provider Premium"}
                </h2>

                <h1 style={styles.price}>
                    ₦{prices[plan][billing].toLocaleString()}
                    <span style={{ fontSize: 16 }}> / {billing}</span>
                </h1>

                {/* BILLING */}
                <div style={styles.billing}>
                    {["monthly", "yearly"].map((b) => (
                        <button
                            key={b}
                            style={{
                                ...styles.billingBtn,
                                background: billing === b ? "#007AFF" : "#eee",
                                color: billing === b ? "#fff" : "#333",
                            }}
                            onClick={() => setBilling(b)}
                        >
                            {b === "yearly" ? "Yearly (Save 10%)" : "Monthly"}
                        </button>
                    ))}
                </div>

                {/* FEATURES */}
                <div style={styles.features}>
                    {features[plan].map((f, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            style={styles.featureItem}
                        >
                            <div style={styles.iconBox}>✓</div>
                            <p style={styles.featureText}>{f}</p>
                        </motion.div>
                    ))}
                </div>

                {/* CTA BUTTON */}
                <motion.button
                    whileHover={{ scale: userId && walletActive ? 1.05 : 1 }}
                    whileTap={{ scale: userId && walletActive ? 0.95 : 1 }}
                    style={{
                        ...styles.cta,
                        opacity: userId && walletActive ? 1 : 0.5,
                        cursor: userId && walletActive ? "pointer" : "not-allowed",
                    }}
                    onClick={() => {
                        if (!userId) {
                            setError("Enter Premium Account ID");
                            return;
                        }
                        if (!walletActive) {
                            setError("Wallet not active");
                            return;
                        }
                        setShowModal(true);
                    }}
                >
                    Upgrade Now
                </motion.button>
            </motion.div>

            {/* MODAL */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        style={styles.overlay}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            initial={{ y: 100 }}
                            animate={{ y: 0 }}
                            exit={{ y: 100 }}
                            style={styles.modal}
                        >
                            <div style={styles.summaryBox}>
                                <h2 style={styles.summaryTitle}>Confirm Subscription</h2>

                                <div style={styles.summaryRow}>
                                    <span>Plan</span>
                                    <strong>{plan}</strong>
                                </div>

                                <div style={styles.summaryRow}>
                                    <span>Billing</span>
                                    <strong>{billing}</strong>
                                </div>

                                <div style={styles.summaryRow}>
                                    <span>Subscription</span>
                                    <strong>₦{prices[plan][billing].toLocaleString()}</strong>
                                </div>

                                <div style={styles.summaryRow}>
                                    <span>Wallet Fee</span>
                                    <strong>₦100.00</strong>
                                </div>

                                <div style={styles.divider} />

                                <div style={styles.summaryTotal}>
                                    <span>Total</span>
                                    <strong>
                                        ₦{(prices[plan][billing] + 100).toLocaleString()}
                                    </strong>
                                </div>

                                <p style={styles.walletBalance}>
                                    Wallet Balance: ₦{formattedBalance}
                                </p>
                            </div>

                            {/* Animated Error */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -10 }}
                                        style={{
                                            background: "#ffe6e6",
                                            color: "#b00020",
                                            padding: "10px 12px",
                                            borderRadius: 8,
                                            marginBottom: 10,
                                            fontWeight: 500,
                                            fontSize: 14,
                                        }}
                                    >
                                        {error}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <input
                                type="password"
                                placeholder="••••"
                                value={pin}
                                onChange={(e) => {
                                    setPin(e.target.value);
                                    if (error) setError("");
                                }}
                                maxLength={4}
                                style={styles.pinInput}
                            />

                            <motion.button
                                style={{
                                    ...styles.payBtn,
                                    background: hasEnoughBalance ? "#007AFF" : "#ccc",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 8,
                                }}
                                onClick={handleSubscribe}
                                disabled={!hasEnoughBalance || processing}
                                whileTap={{ scale: processing ? 1 : 0.97 }}
                            >
                                {processing ? (
                                    <>
                                        <motion.div
                                            style={{
                                                width: 18,
                                                height: 18,
                                                border: "2px solid #fff",
                                                borderTop: "2px solid transparent",
                                                borderRadius: "50%",
                                            }}
                                            animate={{ rotate: 360 }}
                                            transition={{
                                                repeat: Infinity,
                                                duration: 0.8,
                                                ease: "linear",
                                            }}
                                        />
                                        Processing...
                                    </>
                                ) : hasEnoughBalance ? (
                                    "Confirm & Pay"
                                ) : (
                                    "Insufficient Funds"
                                )}
                            </motion.button>

                            <button onClick={() => setShowModal(false)} style={styles.cancel}>
                                Cancel
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <AnimatePresence>
                {processing && (
                    <motion.div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.7)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 1000,
                            flexDirection: "column",
                            color: "#fff",
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            style={{
                                width: 50,
                                height: 50,
                                border: "4px solid #fff",
                                borderTop: "4px solid transparent",
                                borderRadius: "50%",
                                marginBottom: 16,
                            }}
                            animate={{ rotate: 360 }}
                            transition={{
                                repeat: Infinity,
                                duration: 1,
                                ease: "linear",
                            }}
                        />

                        <p style={{ fontSize: 16, fontWeight: "500" }}>
                            Processing your payment...
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* SUCCESS MODAL */}
            <AnimatePresence>
                {paymentSuccess && (
                    <motion.div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.6)",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            zIndex: 999,
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.8, opacity: 0 }}
                            style={{
                                background: "#fff",
                                color: "#111",
                                borderRadius: 16,
                                padding: 30,
                                textAlign: "center",
                            }}
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: "spring", stiffness: 300 }}
                                style={{ fontSize: 40, marginBottom: 16 }}
                            >
                                🎉
                            </motion.div>
                            <p style={{ fontSize: 18, fontWeight: "600" }}>
                                {plan} Premium Activated!
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

const styles = {
    page: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #2a3246, #6586df)",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "clamp(16px, 4vw, 24px)",
        paddingTop: "clamp(70px, 12vw, 100px)"
    },

    hero: {
        textAlign: "center",
        marginTop: "clamp(20px, 5vw, 60px)",
        maxWidth: 600,
    },

    title: {
        fontSize: "clamp(26px, 5vw, 42px)",
        fontWeight: "800",
        lineHeight: 1.2,
    },

    subtitle: {
        opacity: 0.85,
        fontSize: "clamp(14px, 2.5vw, 18px)",
        marginTop: 8,
    },

    switch: {
        display: "flex",
        flexWrap: "wrap", //new
        background: "#ffffff22",
        borderRadius: 14,
        marginTop: 30,
        width: "100%",
        maxWidth: 400,
    },

    switchBtn: {
        flex: 1,
        padding: "12px",
        border: "none",
        cursor: "pointer",
        borderRadius: 12,
        fontSize: "clamp(14px, 2.5vw, 16px)",
    },

    userSection: {
        width: "100%",
        // maxWidth: 420,
        maxWidth: "min(420px, 100%)",
        marginTop: 20,
        display: "flex",
        flexDirection: "column",
        gap: 12,
    },

    input: {
        padding: "14px",
        borderRadius: 12,
        border: "none",
        outline: "none",
        // fontSize: 15,        
        fontSize: "clamp(16px, 2.5vw, 18px)",
        color: "#000"
    },

    walletCard: {
        background: "#ffffff22",
        padding: 16,
        borderRadius: 14,
        backdropFilter: "blur(10px)",
    },

    walletStatus: {
        fontSize: 14,
        opacity: 0.8,
        marginTop: 4,
    },

    eligibility: {
        marginTop: 10,
        padding: "8px 10px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 500,
    },

    summaryBox: {
        marginTop: 10,
        background: "#f8fafc",
        padding: "16px",
        borderRadius: 12,
        textAlign: "left",
    },

    summaryTitle: {
        marginBottom: 12,
        fontSize: 18,
        fontWeight: "700",
        textAlign: "center",
    },

    summaryRow: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: 8,
        fontSize: 14,
        color: "#333",
    },

    divider: {
        height: 1,
        background: "#e5e7eb",
        margin: "10px 0",
    },

    summaryTotal: {
        display: "flex",
        justifyContent: "space-between",
        fontSize: 16,
        fontWeight: "700",
    },

    walletBalance: {
        marginTop: 10,
        fontSize: 13,
        color: "#555",
        textAlign: "center",
    },

    pinInput: {
        marginTop: 10,
        padding: "12px",
        borderRadius: 10,
        border: "1px solid #ddd",
        textAlign: "center",
        width: "100%",
        // fontSize: 18,
        fontSize: "clamp(16px, 4vw, 18px)",
        letterSpacing: 6,
    },

    card: {
        background: "#fff",
        color: "#111",
        borderRadius: 20,
        padding: "clamp(20px, 4vw, 32px)",
        marginTop: 30,
        width: "100%",
        maxWidth: 420,
        textAlign: "center",
        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    },

    planTitle: {
        fontWeight: "700",
        fontSize: "clamp(16px, 3vw, 20px)",
    },

    price: {
        fontSize: "clamp(28px, 6vw, 36px)",
        margin: "10px 0",
    },

    billing: {
        display: "flex",
        gap: 10,
        justifyContent: "center",
        marginBottom: 20,
        flexWrap: "wrap",
    },

    billingBtn: {
        padding: "10px 14px",
        borderRadius: 10,
        border: "none",
        cursor: "pointer",
        fontSize: "clamp(13px, 2.5vw, 15px)",
    },

    features: {
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginBottom: 24,
    },

    featureItem: {
        display: "flex",
        alignItems: "center",
        gap: 12,
        background: "#f8fafc",
        padding: "12px 14px",
        borderRadius: 12,
        transition: "all 0.2s ease",
        flexWrap: "wrap", //new
    },

    iconBox: {
        minWidth: 28,
        height: 28,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #007AFF, #3399FF)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#fff",
        fontSize: 14,
        fontWeight: "bold",
    },

    featureText: {
        margin: 0,
        fontSize: "clamp(14px, 2.5vw, 16px)",
        color: "#333",
    },

    cta: {
        background: "#007AFF",
        color: "#fff",
        padding: "14px",
        borderRadius: 12,
        border: "none",
        cursor: "pointer",
        width: "100%",
        fontWeight: "700",
        fontSize: "clamp(14px, 2.5vw, 16px)",
    },

    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0,0,0,0.6)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: 16,
    },

    modal: {
        background: "#fff",
        color: "#111",
        padding: "clamp(20px, 4vw, 30px)",
        borderRadius: 16,
        textAlign: "center",
        width: "100%",
        maxWidth: 360,
        maxHeight: "90vh", //new
        overflowY: "auto",  //new
    },

    payBtn: {
        background: "#007AFF",
        color: "#fff",
        padding: 14,
        borderRadius: 10,
        border: "none",
        width: "100%",
        marginTop: 10,
        fontSize: 15,
    },

    cancel: {
        marginTop: 12,
        background: "transparent",
        border: "none",
        color: "#666",
        cursor: "pointer",
        fontSize: 14,
    },
};